import { useState, useEffect } from "react";
import { useSession } from "@clerk/clerk-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { toastError } from "@/lib/userMessages";
import { CircleNotch as Loader2, Eye, Code, ArrowUp, ArrowDown, X, Plus } from "@phosphor-icons/react";
import { adminAgentsApi, type AgentMeta, type AgentDetail } from "../api/adminAgents";
import {
  ACTIVE_OPENAI_MODELS,
  DEFAULT_OPENAI_MODEL,
  OPENAI_ONLY_MODE,
  OPENAI_REASONING_EFFORT,
  normalizeAdminModelPriority,
} from "@/lib/ai/providerPolicy";

const ALL_MODELS = [
  ...ACTIVE_OPENAI_MODELS.map((model) => model.id),
  "gpt-4o",
  "gpt-4o-mini",
  "gpt-5.4",
  "gpt-5.4-mini",
  "gpt-5.5",
  "gpt-5.5-mini",
  "claude-haiku-4-5",
  "claude-sonnet-4-6",
  "claude-opus-4-7",
  "gemini-2.5-flash",
  "gemini-2.5-flash-lite",
  "gemini-2.5-pro",
  "gemini-3-flash-preview",
  "gemini-3.1-flash-lite-preview",
  "gemini-3.1-pro-preview",
];

interface Props {
  open: boolean;
  agent: AgentMeta | null;
  isNew?: boolean;
  onClose: () => void;
  onSaved: () => void;
}

function PriorityListEditor({
  value,
  onChange,
}: {
  value: string[];
  onChange: (v: string[]) => void;
}) {
  const available = ALL_MODELS.filter((m) => !value.includes(m));
  const [toAdd, setToAdd] = useState<string>("");

  function move(idx: number, dir: -1 | 1) {
    const next = [...value];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange(next);
  }

  function remove(idx: number) {
    onChange(value.filter((_, i) => i !== idx));
  }

  function add() {
    if (!toAdd || value.includes(toAdd)) return;
    onChange([...value, toAdd]);
    setToAdd("");
  }

  return (
    <div className="space-y-2">
      {value.map((model, idx) => (
        <div
          key={model}
          className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border/50 bg-card"
        >
          <span className="text-xs text-muted-foreground w-4 text-center shrink-0">{idx + 1}</span>
          <span className="font-mono text-xs flex-1 truncate">{model}</span>
          <div className="flex items-center gap-0.5 shrink-0">
            <button
              type="button"
              onClick={() => move(idx, -1)}
              disabled={idx === 0}
              className="p-1 rounded hover:bg-muted disabled:opacity-30 transition-colors"
              aria-label="Mover para cima"
            >
              <ArrowUp className="w-3 h-3" />
            </button>
            <button
              type="button"
              onClick={() => move(idx, 1)}
              disabled={idx === value.length - 1}
              className="p-1 rounded hover:bg-muted disabled:opacity-30 transition-colors"
              aria-label="Mover para baixo"
            >
              <ArrowDown className="w-3 h-3" />
            </button>
            <button
              type="button"
              onClick={() => remove(idx)}
              disabled={value.length <= 1}
              className="p-1 rounded hover:bg-destructive/15 hover:text-destructive disabled:opacity-30 transition-colors"
              aria-label="Remover modelo"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        </div>
      ))}

      {available.length > 0 && (
        <div className="flex items-center gap-2">
          <Select value={toAdd} onValueChange={setToAdd}>
            <SelectTrigger className="flex-1 h-8 text-xs">
              <SelectValue placeholder="Adicionar modelo…" />
            </SelectTrigger>
            <SelectContent>
              {available.map((m) => (
                <SelectItem key={m} value={m} className="font-mono text-xs">{m}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 px-2"
            onClick={add}
            disabled={!toAdd}
          >
            <Plus className="w-3.5 h-3.5" />
          </Button>
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        O servidor testa cada modelo em ordem e usa o primeiro com chave de API configurada.
      </p>
    </div>
  );
}

export function AgentDrawer({ open, agent, isNew, onClose, onSaved }: Props) {
  const { session } = useSession();
  const [loading, setLoading] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [promptMode, setPromptMode] = useState<"edit" | "preview">("edit");

  const [form, setForm] = useState({
    id: "",
    name: "",
    description: "",
    modelPriority: [DEFAULT_OPENAI_MODEL] as string[],
    temperature: "0.7",
    system: "",
  });

  useEffect(() => {
    if (!open) return;
    if (isNew || !agent) {
      setForm({ id: "", name: "", description: "", modelPriority: [DEFAULT_OPENAI_MODEL], temperature: "0.7", system: "" });
      setPromptMode("edit");
      return;
    }
    setLoadingDetail(true);
    session?.getToken().then(async (token) => {
      if (!token) return;
      try {
        const detail: AgentDetail = await adminAgentsApi.get(agent.id, token);
        const storedPriority =
          detail.model_priority && detail.model_priority.length > 0
            ? detail.model_priority
            : [detail.model ?? DEFAULT_OPENAI_MODEL];
        const priority = normalizeAdminModelPriority(storedPriority);
        setForm({
          id: detail.id,
          name: detail.name ?? agent?.name ?? "",
          description: detail.description ?? "",
          modelPriority: priority,
          temperature: String(detail.temperature ?? "0.7"),
          system: detail.system ?? "",
        });
        setPromptMode("edit");
      } catch {
        toast.error("Erro ao carregar prompt do agente");
      } finally {
        setLoadingDetail(false);
      }
    });
  }, [open, agent, isNew, session]);

  function set(field: string, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSave() {
    if (!form.name.trim()) { toast.error("Nome obrigatório"); return; }
    if (!form.system.trim()) { toast.error("System prompt obrigatório"); return; }
    if (isNew && !form.id.trim()) { toast.error("ID obrigatório"); return; }
    if (form.modelPriority.length === 0) { toast.error("Adicione ao menos um modelo"); return; }

    const token = await session?.getToken();
    if (!token) return;

    setLoading(true);
    try {
      const payload = {
        name: form.name,
        description: form.description,
        model: form.modelPriority[0],
        model_priority: form.modelPriority,
        temperature: parseFloat(form.temperature),
        system: form.system,
      };
      if (isNew) {
        await adminAgentsApi.create(form.id, payload, token);
        toast.success("Agente criado");
      } else if (agent) {
        await adminAgentsApi.update(agent.id, payload, token);
        toast.success("Agente atualizado — nova versão criada");
      }
      onSaved();
      onClose();
    } catch (e) {
      toastError(e, "Não foi possível salvar o agente. Tente novamente.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent className="w-full sm:max-w-2xl flex flex-col p-0 gap-0">
        {/* Sticky header */}
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-border shrink-0">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <SheetTitle className="flex items-center gap-2 mb-3">
                {isNew ? "Novo Agente" : `Editar: ${agent?.id}`}
                {agent && !isNew && (
                  <Badge variant="outline" className="text-xs font-mono shrink-0">
                    v{agent.version} → v{(agent.version ?? 0) + 1}
                  </Badge>
                )}
              </SheetTitle>
              {!loadingDetail && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Nome</Label>
                    <Input
                      value={form.name}
                      onChange={(e) => set("name", e.target.value)}
                      placeholder="Nome do agente"
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Descrição</Label>
                    <Input
                      value={form.description}
                      onChange={(e) => set("description", e.target.value)}
                      placeholder="Descrição opcional"
                      className="h-8 text-sm"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </SheetHeader>

        {/* Scrollable body */}
        {loadingDetail ? (
          <div className="flex items-center justify-center flex-1">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
            {/* ID — new only */}
            {isNew && (
              <div className="space-y-1.5">
                <Label>
                  ID <span className="text-muted-foreground text-xs">(imutável após criação)</span>
                </Label>
                <Input
                  placeholder="ex: roteiros.creation.system"
                  value={form.id}
                  onChange={(e) => set("id", e.target.value)}
                  className="font-mono text-sm"
                />
              </div>
            )}

            {/* Temperature */}
            <div className="space-y-1.5">
              {OPENAI_ONLY_MODE ? (
                <div className="rounded-lg border border-border/50 bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
                  GPT-5.6 usa raciocínio <strong className="text-foreground">high</strong>.
                  Temperatura fica preservada apenas para eventual retorno do modo legado.
                </div>
              ) : (
                <>
                  <Label>
                    Temperatura{" "}
                    <span className="text-muted-foreground text-xs">({form.temperature})</span>
                  </Label>
                  <input
                    type="range"
                    min="0"
                    max="2"
                    step="0.05"
                    value={form.temperature}
                    onChange={(e) => set("temperature", e.target.value)}
                    className="w-full"
                  />
                </>
              )}
            </div>

            {/* Active model */}
            <div className="space-y-2">
              <Label>{OPENAI_ONLY_MODE ? "Modelo ativo (OpenAI)" : "Prioridade de Modelos"}</Label>
              {OPENAI_ONLY_MODE ? (
                <div className="space-y-2">
                  <Select
                    value={form.modelPriority[0] ?? DEFAULT_OPENAI_MODEL}
                    onValueChange={(model) => setForm((prev) => ({ ...prev, modelPriority: [model] }))}
                  >
                    <SelectTrigger className="font-mono text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ACTIVE_OPENAI_MODELS.map((model) => (
                        <SelectItem key={model.id} value={model.id}>
                          <span className="font-mono text-xs">{model.id}</span>
                          <span className="ml-2 text-xs text-muted-foreground">— {model.profile}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                    <Badge variant="outline">Provedor: OpenAI</Badge>
                    <Badge variant="outline">Raciocínio: {OPENAI_REASONING_EFFORT}</Badge>
                    <span>Somente este modelo será usado por este agente.</span>
                  </div>
                </div>
              ) : (
                <PriorityListEditor
                  value={form.modelPriority}
                  onChange={(v) => setForm((prev) => ({ ...prev, modelPriority: v }))}
                />
              )}
            </div>

            {/* System prompt */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>
                  System Prompt <span className="text-destructive">*</span>
                </Label>
                <div className="flex items-center border border-border rounded-md overflow-hidden text-xs">
                  <button
                    onClick={() => setPromptMode("edit")}
                    className={`px-3 py-1.5 flex items-center gap-1.5 transition-colors ${
                      promptMode === "edit"
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground hover:bg-muted"
                    }`}
                  >
                    <Code className="w-3 h-3" /> Raw
                  </button>
                  <button
                    onClick={() => setPromptMode("preview")}
                    className={`px-3 py-1.5 flex items-center gap-1.5 transition-colors ${
                      promptMode === "preview"
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground hover:bg-muted"
                    }`}
                  >
                    <Eye className="w-3 h-3" /> Preview
                  </button>
                </div>
              </div>

              {promptMode === "edit" ? (
                <Textarea
                  value={form.system}
                  onChange={(e) => set("system", e.target.value)}
                  className="font-mono text-xs min-h-[320px] resize-y leading-relaxed"
                  placeholder="Você é um assistente especializado em..."
                />
              ) : (
                <div className="border border-border rounded-md bg-muted/20 min-h-[320px] max-h-[60vh] overflow-y-auto">
                  {form.system ? (
                    <pre className="p-4 text-sm text-foreground whitespace-pre-wrap leading-relaxed font-sans break-words">
                      {form.system}
                    </pre>
                  ) : (
                    <p className="p-4 text-muted-foreground italic text-sm">Nenhum conteúdo</p>
                  )}
                </div>
              )}
            </div>

            {/* Info banner */}
            <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-primary/5 border border-primary/15 text-xs text-muted-foreground">
              <span className="text-primary mt-0.5">ℹ</span>
              <span>
                Contexto do perfil do usuário é injetado automaticamente no início de cada chamada pelo servidor. Não é necessário incluir no prompt.
              </span>
            </div>
          </div>
        )}

        {/* Sticky footer */}
        <div className="shrink-0 px-6 py-4 border-t border-border flex justify-end gap-2 bg-background">
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSave} disabled={loading || loadingDetail}>
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {isNew ? "Criar Agente" : "Salvar Nova Versão"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
